import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X, Search } from 'lucide-react';
import { SelectColumn } from '../../interfaces/sessions';
import { Typography } from '../../interfaces/typography';

export interface MultiSelectOption {
  value: string;
  label: string;
  column: SelectColumn;
}

interface FilterMultiSelectProps {
  options: MultiSelectOption[]
  onChange: (values: MultiSelectOption[]) => void;
  selected: MultiSelectOption[];
  placeholder?: string;
}

export function FilterMultiSelect({ options, selected, onChange, placeholder = 'Select options...' }: FilterMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset component state when options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [JSON.stringify(options)]);

  // Explicit filtering logic
  const filteredOptions = React.useMemo(() => {
    return options.filter(option => {
      if (search === '') return true;
      return option.label.toLowerCase().includes(search.toLowerCase());
    });
  }, [options, search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      const option = filteredOptions[focusedIndex];
      toggleOption(option);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const values = selected.map((v) => v.value)
  const toggleOption = (option: MultiSelectOption) => {
    // Don't toggle special values
    const optionValue = option.value;
    if (optionValue.includes('_SEARCH_') || optionValue.includes('_SEARCHING_') || optionValue.includes('_NO_RESULTS_')) {
      return;
    }
    const newValue = values.includes(optionValue)
      ? selected.filter(v => v.value !== optionValue)
      : [...selected, option];
    onChange(newValue);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (newSearch: string) => setSearch(newSearch);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="border rounded-lg bg-white px-2 py-1.5 min-h-[38px] cursor-pointer flex flex-wrap gap-1 items-center"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <div className="flex flex-wrap gap-1 items-center flex-1 min-w-0">
          {selected.length > 0 ? (
            selected.map((option) => {
              if (!option) return null;
              return (
                <span
                  key={option.value}
                  className="bg-blue-100 text-blue-800 rounded px-1.5 py-0.5 flex items-center gap-1 max-w-full"
                >
                  <Typography variant="smallParagraph" color="#1e40af" className="truncate">
                    {option.label}
                  </Typography>
                  <X
                    size={14}
                    className="shrink-0 cursor-pointer hover:text-blue-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(option);
                    }}
                  />
                </span>
              );
            })
          ) : (
            <Typography variant="mediumParagraph" color="#6B7280" className="truncate">
              {placeholder}
            </Typography>
          )}
        </div>
        <ChevronDown size={18} className="shrink-0 text-gray-400 ml-auto" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60">
          <div className="p-1.5 border-b">
            <div className="relative">
              <Search size={18} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                className="w-full pl-7 pr-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search options..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  fontSize: '13px',
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif'
                }}
              />
            </div>
          </div>
          <div className="max-h-40 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-3 text-center">
                <Typography variant="mediumParagraph" color="#6B7280">
                  No options found
                </Typography>
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                return (
                  <div
                    key={`${option.value}-${index}`}
                    className={`px-2 py-1.5 cursor-pointer flex items-center gap-2 ${
                      focusedIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleOption(option)}
                  >
                    <div className={`shrink-0 w-4 h-4 border rounded flex items-center justify-center ${
                      values.includes(option.value) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}>
                      {values.includes(option.value) && <Check size={12} className="text-white" />}
                    </div>
                    <Typography variant="mediumParagraph" color="#111827" className="truncate">
                      {option.label}
                    </Typography>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}